export const DESTRUCTIVE_AD_EDGE_TYPES = [] as const;

export type DestructiveAdEdgeType = (typeof DESTRUCTIVE_AD_EDGE_TYPES)[number];

export const TECHNICALLY_IMPOSSIBLE_AD_EDGE_TYPES = [
  'CanRDP',
  'ExecuteDCOM',
  'DumpSMSAPassword',
  'SyncLAPSPassword',
  'SQLAdmin',
  'AddKeyCredentialLink',
  'WriteAccountRestrictions',
  'WriteGPLink',
  'WriteOwnerLimitedRights',
  'OwnsLimitedRights',
  'SpoofSIDHistory',
] as const;

export type TechnicallyImpossibleAdEdgeType =
  (typeof TECHNICALLY_IMPOSSIBLE_AD_EDGE_TYPES)[number];

export const INFORMATIONAL_AD_EDGE_TYPES = [
  'HasSession',
  'HasSIDHistory',
  'SyncedToADUser',
  'ClaimSpecialIdentity',
  'ContainsIdentity',
  'PropagatesACEsTo',
  'GPOAppliesTo',
  'CanApplyGPO',
  'HasTrustKeys',
  'DCFor',
  'SameForestTrust',
  'Contains',
  'GPLink',
] as const;

export type InformationalAdEdgeType =
  (typeof INFORMATIONAL_AD_EDGE_TYPES)[number];

export const KNOWN_AD_RELATIONSHIP_TYPES = [
  'AdminTo',
  'GenericAll',
  'GenericWrite',
  'WriteOwner',
  'WriteDacl',
  'MemberOf',
  'ForceChangePassword',
  'AllExtendedRights',
  'AddMember',
  'HasSession',
  'GPLink',
  'AllowedToDelegate',
  'CoerceToTGT',
  'AllowedToAct',
  'DCSync',
  'ReadLAPSPassword',
  'ReadGMSAPassword',
  'DumpSMSAPassword',
  'SQLAdmin',
  'AddAllowedToAct',
  'WriteSPN',
  'AddKeyCredentialLink',
  'SyncLAPSPassword',
  'WriteAccountRestrictions',
  'WriteGPLink',
  'GoldenCert',
  'ADCSESC1',
  'ADCSESC3',
  'ADCSESC4',
  'ADCSESC6a',
  'ADCSESC6b',
  'ADCSESC9a',
  'ADCSESC9b',
  'ADCSESC10a',
  'ADCSESC10b',
  'ADCSESC13',
  'SyncedToADUser',
  'CoerceAndRelayNTLMToSMB',
  'CoerceAndRelayNTLMToADCS',
  'WriteOwnerLimitedRights',
  'OwnsLimitedRights',
  'ClaimSpecialIdentity',
  'CoerceAndRelayNTLMToLDAP',
  'CoerceAndRelayNTLMToLDAPS',
  'ContainsIdentity',
  'PropagatesACEsTo',
  'GPOAppliesTo',
  'CanApplyGPO',
  'HasTrustKeys',
  'ManageCA',
  'ManageCertificates',
  'Contains',
  'DCFor',
  'SameForestTrust',
  'SpoofSIDHistory',
  'AbuseTGTDelegation',
  'CanPSRemote',
  'CanRDP',
  'ExecuteDCOM',
  'HasSIDHistory',
  'AddSelf',
  'Owns',
  'CanKerberoast',
  'CanASREPRoast',
] as const;

export type KnownAdRelationshipType =
  (typeof KNOWN_AD_RELATIONSHIP_TYPES)[number];

const destructiveEdgeTypes = new Set<string>(DESTRUCTIVE_AD_EDGE_TYPES);
const technicallyImpossibleEdgeTypes = new Set<string>(
  TECHNICALLY_IMPOSSIBLE_AD_EDGE_TYPES,
);
const informationalEdgeTypes = new Set<string>(INFORMATIONAL_AD_EDGE_TYPES);

export const ACTIONABLE_AD_EDGE_TYPES = KNOWN_AD_RELATIONSHIP_TYPES.filter(
  (edgeType) =>
    !destructiveEdgeTypes.has(edgeType) &&
    !technicallyImpossibleEdgeTypes.has(edgeType) &&
    !informationalEdgeTypes.has(edgeType),
) as readonly Exclude<
  KnownAdRelationshipType,
  | DestructiveAdEdgeType
  | TechnicallyImpossibleAdEdgeType
  | InformationalAdEdgeType
>[];

export const ABUSABLE_AD_RELATIONSHIP_TYPES = ACTIONABLE_AD_EDGE_TYPES;

export type AbusableAdRelationshipType =
  (typeof ABUSABLE_AD_RELATIONSHIP_TYPES)[number];

// Containment, replication and trust edges describe where an object lives rather than a
// privilege an attacker can use. Traversing them links every object to its OU siblings,
// which collapses unrelated principals into a single "shortest path".
export const STRUCTURAL_AD_EDGE_TYPES = [
  'Contains',
  'GPLink',
  'DCFor',
  'SameForestTrust',
  'ContainsIdentity',
  'PropagatesACEsTo',
  'GPOAppliesTo',
  'CanApplyGPO',
  'HasTrustKeys',
  'ClaimSpecialIdentity',
  'SyncedToADUser',
] as const;

export type StructuralAdEdgeType = (typeof STRUCTURAL_AD_EDGE_TYPES)[number];

const structuralEdgeTypes = new Set<string>(STRUCTURAL_AD_EDGE_TYPES);

export const isStructuralAdEdgeType = (relationshipType: string): boolean =>
  structuralEdgeTypes.has(relationshipType);

// Membership edges confer their target's rights to the member intrinsically (the
// member's Kerberos/NTLM token already carries the group SID). There is no C2
// technique to "exploit" them, so they must never be bound as an executable hop.
const INTRINSIC_MEMBERSHIP_AD_EDGE_TYPES = new Set<string>([
  'MemberOf',
  'MemberOfLocalGroup',
]);

export const isIntrinsicTraversalAdEdgeType = (
  relationshipType: string,
): boolean =>
  structuralEdgeTypes.has(relationshipType) ||
  INTRINSIC_MEMBERSHIP_AD_EDGE_TYPES.has(relationshipType);

// Reachability, not executability: an edge belongs here when an attacker could follow it,
// even when no C2 tool can automate it. ACTIONABLE_AD_EDGE_TYPES answers the narrower
// "can we execute this ourselves" question and must not be used for path discovery.
export const PATH_TRAVERSAL_AD_EDGE_TYPES = KNOWN_AD_RELATIONSHIP_TYPES.filter(
  (edgeType) => !structuralEdgeTypes.has(edgeType),
) as readonly Exclude<KnownAdRelationshipType, StructuralAdEdgeType>[];

export type PathTraversalAdEdgeType =
  (typeof PATH_TRAVERSAL_AD_EDGE_TYPES)[number];

export const isPathTraversableAdEdgeType = (
  relationshipType: string,
): boolean => !structuralEdgeTypes.has(relationshipType);

export const buildPathTraversalAdEdgeCypherTypes = (): string =>
  PATH_TRAVERSAL_AD_EDGE_TYPES.join('|');

export const isDestructiveAdEdgeType = (relationshipType: string): boolean =>
  destructiveEdgeTypes.has(relationshipType);

export const isTechnicallyImpossibleAdEdgeType = (
  relationshipType: string,
): boolean => technicallyImpossibleEdgeTypes.has(relationshipType);

export const isInformationalAdEdgeType = (relationshipType: string): boolean =>
  informationalEdgeTypes.has(relationshipType);

export const isActionableAdEdgeType = (relationshipType: string): boolean =>
  !isDestructiveAdEdgeType(relationshipType) &&
  !isTechnicallyImpossibleAdEdgeType(relationshipType) &&
  !isInformationalAdEdgeType(relationshipType);

export const isBindableAdHopEdgeType = (relationshipType: string): boolean =>
  isActionableAdEdgeType(relationshipType) &&
  !isIntrinsicTraversalAdEdgeType(relationshipType);

export const BINDABLE_AD_HOP_EDGE_TYPES = ACTIONABLE_AD_EDGE_TYPES.filter(
  (edgeType) => !isIntrinsicTraversalAdEdgeType(edgeType),
);

export const buildBindableAdHopEdgeCypherTypes = (): string =>
  BINDABLE_AD_HOP_EDGE_TYPES.join('|');

export const buildActionableAdEdgeCypherTypes = (): string =>
  ACTIONABLE_AD_EDGE_TYPES.join('|');

export const OBJECT_CONTROL_EXCLUDED_EDGE_TYPES = [
  'MemberOf',
  'Contains',
  'HasSession',
  'GPLink',
  'HasSIDHistory',
  'SyncedToADUser',
  'ContainsIdentity',
  'PropagatesACEsTo',
  'GPOAppliesTo',
  'CanApplyGPO',
  'HasTrustKeys',
  'DCFor',
  'SameForestTrust',
] as const;

export type ObjectControlExcludedEdgeType =
  (typeof OBJECT_CONTROL_EXCLUDED_EDGE_TYPES)[number];

export const BLOODHOUND_PRE_PROCESSING_RAW_EDGE_TYPES = [
  'OwnsRaw',
  'WriteOwnerRaw',
] as const;

const preProcessingRawEdgeTypes = new Set<string>(
  BLOODHOUND_PRE_PROCESSING_RAW_EDGE_TYPES,
);

export const isPreProcessingRawAdEdgeType = (
  relationshipType: string,
): boolean => preProcessingRawEdgeTypes.has(relationshipType);

export const EXECUTION_PRIVILEGE_AD_EDGE_TYPES = [
  'CanPSRemote',
  'ExecuteDCOM',
  'CanRDP',
] as const;

export type ExecutionPrivilegeAdEdgeType =
  (typeof EXECUTION_PRIVILEGE_AD_EDGE_TYPES)[number];

export const buildObjectControlExcludedEdgeCypherTypes = (): string =>
  OBJECT_CONTROL_EXCLUDED_EDGE_TYPES.join('|');

export const buildExecutionPrivilegeEdgeCypherTypes = (): string =>
  EXECUTION_PRIVILEGE_AD_EDGE_TYPES.join('|');

export const getTechnicallyImpossibleEdgeBlockReason = ({
  relationshipType,
}: {
  relationshipType: string;
}): string => `No C2 tool available for edge type ${relationshipType}`;
